import React, { useState } from 'react';
import styled from '@emotion/styled';
import { TestId } from '@components/test-ids';
import { Checkbox, CheckboxResponse } from '@components/common/checkbox';
import { Filter, FilterOption } from '@model/iceberg';
import { useI18NextContext } from '@components/hooks';
import { mixins } from '@styles/mixins';
import { ThemeProps, ExpandableThemeProps, SelectableThemeProps } from '@theme/base';
import { getFilterPaddingMixin } from '@styles/filters';

/* ********** *
 *    Types   *
 * ********** */

export interface MultiselectFilterProps {
  filter: Filter;
  onChange: (payload: Filter) => void;
  visibleNumberOfFilters?: number;
  testId?: string;
  renderLabel: (filterOption: FilterOption) => JSX.Element;
  isLeftFilter?: boolean;
}

/* *********** *
 *    Styles   *
 * *********** */

export const FilterRowLabelWrapper = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: 3,

  ['&:hover']: {
    [FilterRowTextLabel]: {
      color: theme.custom.colors.group1.base
    }
  }
}));

interface ContainerProps extends ThemeProps {
  isLeftFilter?: boolean;
}

const Container: any = styled.div(({ theme, isLeftFilter }: ContainerProps) => ({
  maxWidth: 375,
  ...getFilterPaddingMixin(theme, isLeftFilter),

  ['.MuiIconButton-root']: {
    padding: theme.custom.spacing.xSmall
  },
  ['.MuiFormControlLabel-root']: {
    marginLeft: 0
  }
}));

const FilterHeading = styled.h5(({ theme }: ThemeProps) => ({
  display: 'flex',
  aligItems: 'center',
  justifyContent: 'flex-start',
  ...(theme.custom.typography.labels.default as any),
  lineHeight: '1.8rem',
  margin: 0
}));

const FiltersContainer: any = styled.ul(({ theme, isExpanded }: ExpandableThemeProps) => ({
  padding: 0,
  transition: `margin ${theme.custom.transitions.default}`,
  ...(isExpanded && { marginBottom: 0 })
}));

const FilterRow = styled.li(({ theme }: ThemeProps) => ({
  display: 'block',
  ...(theme.custom.typography.paragraph as any)
}));

interface FilterRowTextLabelProps extends SelectableThemeProps {
  available: boolean;
}
export const FilterRowTextLabel: any = styled.span(({ theme, isSelected, available }: FilterRowTextLabelProps) => ({
  ...(theme.custom.typography.paragraph as any),
  paddingTop: 2,
  transition: `color ${theme.custom.transitions.default}`,
  ...(isSelected && { color: theme.custom.colors.group1.base }),
  ...(!available && { color: theme.custom.colors.group10.dark })
}));

const MoreFiltersToggle: any = styled.li(({ theme, isExpanded }: ExpandableThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  ...mixins(theme).link,
  cursor: 'pointer',
  overflowY: 'hidden',
  maxHeight: isExpanded ? 0 : 20,
  transition: `max-height ${theme.custom.transitions.default} 0.4s, color: ${theme.custom.transitions.default}`,
  marginTop: 10
}));

/* ********************** *
 *    MultiselectFilter   *
 * ********************** */

export const MultiselectFilter = ({
  testId,
  filter,
  onChange,
  visibleNumberOfFilters = 5,
  renderLabel,
  isLeftFilter
}: MultiselectFilterProps) => {
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(false);
  const t: any = useI18NextContext();

  const handleOnSelectAll = () => {
    const values: Array<FilterOption> = filter.values.map((f: FilterOption) => ({ ...f, selected: false }));
    onChange({ ...filter, values });
  };
  const handleOnChange = (payload: CheckboxResponse) => {
    const option = filter.values.filter((f: FilterOption) => f.value === payload.value).shift();
    if (option) {
      if (payload.checked) {
        const selected: Array<FilterOption> = filter.values.filter((f: FilterOption) => f.selected);
        const values: Array<FilterOption> = selected.concat({ ...option, selected: true });
        onChange({ ...filter, values });
      } else {
        const values: Array<FilterOption> = filter.values.filter((f: FilterOption) => f.value !== option.value);
        onChange({ ...filter, values });
      }
    }
  };
  const renderFilterRows = () =>
    filter.values
      .filter((filter: FilterOption, index: number) =>
        showMoreFilters ? !!filter : index + 1 <= visibleNumberOfFilters
      )
      .map((filter: FilterOption, index: number) => {
        return (
          <FilterRow key={`${filter.name}-${index}`}>
            <Checkbox
              testId={TestId.multiselectFilters.filterCheckbox}
              value={filter.value}
              labelComponent={<FilterRowLabelWrapper>{renderLabel(filter)}</FilterRowLabelWrapper>}
              checked={!!filter.selected}
              onChange={handleOnChange}
              available={!!filter.count}
            />
          </FilterRow>
        );
      });
  const isAllSelected: boolean = filter.values.every((f: FilterOption) => !f.selected);
  const numberOfHiddenFilters: number = filter.values.filter(
    (_: FilterOption, index: number) => index + 1 > visibleNumberOfFilters
  ).length;
  return (
    <Container data-testid={testId || TestId.multiselectFilters.main} isLeftFilter={isLeftFilter}>
      <FilterHeading data-testid={TestId.multiselectFilters.header}>{filter.formattedName}</FilterHeading>
      <FiltersContainer isExpanded={showMoreFilters}>
        <FilterRow data-testid={TestId.multiselectFilters.filterRowAll}>
          <Checkbox
            testId={TestId.multiselectFilters.filterCheckbox}
            value={''}
            checked={isAllSelected}
            available={true}
            onChange={handleOnSelectAll}
            labelComponent={
              <FilterRowLabelWrapper>
                <FilterRowTextLabel
                  data-testid={TestId.multiselectFilters.textFilterLabel}
                  isSelected={isAllSelected}
                  available={true}
                >
                  {t('filters__title--All')}
                </FilterRowTextLabel>
              </FilterRowLabelWrapper>
            }
          />
        </FilterRow>
        {renderFilterRows()}
        {!!numberOfHiddenFilters && (
          <MoreFiltersToggle isExpanded={showMoreFilters} onClick={() => setShowMoreFilters(!showMoreFilters)}>
            {t('filters__view-more--title', { count: numberOfHiddenFilters })}
          </MoreFiltersToggle>
        )}
      </FiltersContainer>
    </Container>
  );
};
